<template>
  <div class="mt-4 mb-6">
    <Listbox
      v-model="atsFilter"
      as="div"
      class="flex w-56 flex-col gap-x-2 lg:flex-row lg:items-center"
    >
      <ListboxLabel class="block shrink-0 text-sm font-medium text-gray-700">
        {{ t('company.importer.ats') }}
      </ListboxLabel>
      <div class="relative md:w-full">
        <ListboxButton
          class="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
        >
          <span
            class="ml-2 block truncate capitalize"
            :class="{ 'text-gray-500': !atsFilter }"
            >{{ atsFilter?.label || t('company.importer.allATS') }}</span
          >
          <span
            class="pointer-events-none absolute inset-y-0 right-0 ml-2 flex items-center pr-2"
          >
            <ChevronUpDownIcon
              class="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </span>
        </ListboxButton>

        <transition
          leaveActiveClass="transition ease-in duration-100"
          leaveFromClass="opacity-100"
          leaveToClass="opacity-0"
        >
          <ListboxOptions
            class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
          >
            <ListboxOption
              value=""
              class="hover:bg-indigo-600 hover:text-white"
              :class="[
                !atsFilter ? 'bg-indigo-600 text-white' : 'text-gray-900',
                'relative cursor-default py-2 pr-9 pl-3 select-none',
              ]"
              >{{ t('company.importer.allATS') }}</ListboxOption
            >
            <ListboxOption
              v-for="(ats, index) in importerStore.availableATS"
              :key="index"
              v-slot="{ active, selected }"
              as="template"
              :value="ats"
            >
              <li
                :class="[
                  active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                  'relative cursor-default py-2 pr-9 pl-3 select-none',
                ]"
              >
                <span
                  :class="[
                    selected ? 'font-semibold' : 'font-normal',
                    'block truncate capitalize',
                  ]"
                >
                  {{ ats.label }}
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </transition>
      </div>
    </Listbox>
  </div>

  <List
    v-slot="{ entity: config }"
    :entities="importerStore.configs"
    :page="importerStore.page"
    :loading="importerStore.isLoading"
    :updatePageNumber="updatePageNumber"
    :updatePageSize="updatePageSize"
  >
    <router-link
      :to="`/companies/${config.company.id}/importer?importerId=${config.id}`"
      class="block text-indigo-600 hover:bg-gray-50 focus:underline focus:outline-hidden"
    >
      <div class="flex px-4 py-4 sm:px-6">
        <div class="flex grow items-center justify-between sm:w-1/2">
          <p class="truncate text-sm font-medium text-indigo-600">
            {{ config.company.name }}
            <span
              v-if="config.archived"
              class="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800"
              >Archived</span
            >
          </p>
          <div class="ml-2 flex shrink-0">
            <p class="flex items-center text-sm text-gray-600 capitalize">
              {{ config.ats.replaceAll('-', ' ') }}
            </p>
          </div>
        </div>
        <div class="grid gap-y-1 text-right sm:w-1/2">
          <p
            v-for="site in config.targetSites"
            :key="site.id"
            class="text-xs text-gray-600"
          >
            {{ site.name }}
          </p>
        </div>
      </div>
    </router-link>
  </List>
</template>

<script setup>
import List from '../components/List.vue'
import { useI18n } from 'vue-i18n'
import { useImporterStore } from '../store/importerStore'
import {
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/vue'
import { ChevronUpDownIcon } from '@heroicons/vue/20/solid'
import { onUnmounted, ref, watch } from 'vue'

const importerStore = useImporterStore()
const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})
importerStore.loadConfig()

const atsFilter = ref({ value: '' })

const updatePageSize = (pageSize) => {
  importerStore.page.number = 1
  importerStore.page.size = pageSize
  importerStore.loadConfig(null, null, atsFilter.value.value)
}

const updatePageNumber = (number) => {
  importerStore.page.number = number
  importerStore.loadConfig(null, null, atsFilter.value.value)
}

watch(atsFilter, () => {
  importerStore.loadConfig(null, null, atsFilter.value.value)
})

onUnmounted(() => {
  importerStore.$reset()
})
</script>
